import { Suspense } from 'react';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import { lazyPage } from '@/lib/lazyWithReload';
import { TenantLoginPage } from '@/modules/tenant/pages/auth/TenantLoginPage';
import { TenantForgotPasswordPage } from '@/modules/tenant/pages/auth/TenantForgotPasswordPage';
import { TenantResetPasswordCheckEmailPage } from '@/modules/tenant/pages/auth/TenantResetPasswordCheckEmailPage';
import { TenantResetPasswordPage } from '@/modules/tenant/pages/auth/TenantResetPasswordPage';
import { TenantResetPasswordChangedPage } from '@/modules/tenant/pages/auth/TenantResetPasswordChangedPage';
import { TenantLayout } from '@/modules/tenant/layouts/TenantLayout';
import { TenantProtectedRoute } from '@/components/TenantProtectedRoute';
import { ModuleProvider } from '@/contexts/ModuleContext';
import { ModuleGate } from '@/components/ModuleGate';
import { ReportingAccessGate } from '@/modules/reporting/components/ReportingAccessGate';
import { PageSkeleton } from '@/components/PageSkeleton';

// #552 — the crm_leads pipeline surface is "Deals" in the UI; old /crm/leads
// bookmarks redirect. Component/file names deliberately keep "Lead" (code-level
// rename is out of scope per the enquiry-ingestion spec).
function LegacyLeadRedirect() {
  const { id } = useParams();
  return <Navigate to={`/crm/deals/${id}`} replace />;
}

// All module pages are lazy-loaded via lazyPage(), which recovers from stale
// code-split chunks after a deploy (see @/lib/lazyWithReload).

// ── Tenant core pages (always loaded — small, needed on every visit) ──
const TenantDashboard = lazyPage(() => import('@/modules/tenant/pages/dashboard/TenantDashboard'), 'TenantDashboard');
const TenantProfilePage = lazyPage(() => import('@/modules/tenant/pages/profile/TenantProfilePage'), 'TenantProfilePage');
const EmployeesListPage = lazyPage(() => import('@/modules/tenant/pages/employees/EmployeesListPage'), 'EmployeesListPage');
const EmployeeDetailPage = lazyPage(() => import('@/modules/tenant/pages/employees/EmployeeDetailPage'), 'EmployeeDetailPage');
const RolesPage = lazyPage(() => import('@/modules/tenant/pages/roles/RolesPage'), 'default');
const NotFoundPage = lazyPage(() => import('@/modules/tenant/pages/NotFoundPage'), 'NotFoundPage');
const InviteAcceptPage = lazyPage(() => import('@/modules/tenant/pages/auth/InviteAcceptPage'), 'InviteAcceptPage');

// ── Inventory module pages ──
const InventoryDashboardPage = lazyPage(() => import('@/modules/inventory/pages/dashboard/InventoryDashboardPage'), 'InventoryDashboardPage');
const AdminBoatListPage = lazyPage(() => import('@/modules/inventory/pages/boats/AdminBoatListPage'), 'AdminBoatListPage');
const AdminEngineListPage = lazyPage(() => import('@/modules/inventory/pages/engines/AdminEngineListPage'), 'AdminEngineListPage');
const AdminTrailerListPage = lazyPage(() => import('@/modules/inventory/pages/trailers/AdminTrailerListPage'), 'AdminTrailerListPage');
const BoatDetailPage = lazyPage(() => import('@/modules/inventory/pages/boats/BoatDetailPage'), 'BoatDetailPage');
const EngineDetailPage = lazyPage(() => import('@/modules/inventory/pages/engines/EngineDetailPage'), 'EngineDetailPage');
const TrailerDetailPage = lazyPage(() => import('@/modules/inventory/pages/trailers/TrailerDetailPage'), 'TrailerDetailPage');
const InventorySettingsPage = lazyPage(() => import('@/modules/inventory/pages/settings/InventorySettingsPage'), 'InventorySettingsPage');
const WpSyncPage = lazyPage(() => import('@/modules/inventory/pages/sync/WpSyncPage'), 'WpSyncPage');
const InventoryReportsPage = lazyPage(() => import('@/modules/inventory/pages/reports/InventoryReportsPage'), 'InventoryReportsPage');

// ── Reporting module pages ──
const AiReportingPage = lazyPage(() => import('@/modules/reporting/pages/AiReportingPage'), 'AiReportingPage');

// ── CRM module pages ──
const CrmDashboardPage = lazyPage(() => import('@/modules/crm/pages/dashboard/CrmDashboardPage'), 'CrmDashboardPage');
const CustomersListPage = lazyPage(() => import('@/modules/crm/pages/customers/CustomersListPage'), 'CustomersListPage');
const CustomerDetailPage = lazyPage(() => import('@/modules/crm/pages/customers/CustomerDetailPage'), 'CustomerDetailPage');
const PipelinesPage = lazyPage(() => import('@/modules/crm/pages/pipelines/PipelinesPage'), 'PipelinesPage');
const LeadKanbanPage = lazyPage(() => import('@/modules/crm/pages/leads/LeadKanbanPage'), 'LeadKanbanPage');
const LeadsListPage = lazyPage(() => import('@/modules/crm/pages/leads/LeadsListPage'), 'LeadsListPage');
const LeadDetailPage = lazyPage(() => import('@/modules/crm/pages/leads/LeadDetailPage'), 'LeadDetailPage');
const QuotesListPage = lazyPage(() => import('@/modules/crm/pages/quotes/QuotesListPage'), 'QuotesListPage');
const QuoteDetailPage = lazyPage(() => import('@/modules/crm/pages/quotes/QuoteDetailPage'), 'QuoteDetailPage');
const TradeInAppraisalPage = lazyPage(() => import('@/modules/crm/pages/trade-ins/TradeInAppraisalPage'), 'TradeInAppraisalPage');
const EnquiriesInboxPage = lazyPage(() => import('@/modules/crm/pages/enquiries/EnquiriesInboxPage'), 'EnquiriesInboxPage');
const EnquiryDetailPage = lazyPage(() => import('@/modules/crm/pages/enquiries/detail/EnquiryDetailPage'), 'EnquiryDetailPage');
const ActivityPage = lazyPage(() => import('@/modules/crm/pages/activity/ActivityPage'), 'ActivityPage');

// ── Service module pages ──
const WorkOrdersListPage = lazyPage(() => import('@/modules/service/pages/work-orders/WorkOrdersListPage'), 'WorkOrdersListPage');
const WorkOrderDetailPage = lazyPage(() => import('@/modules/service/pages/work-orders/WorkOrderDetailPage'), 'WorkOrderDetailPage');
const SubcontractorsPage = lazyPage(() => import('@/modules/service/pages/subcontractors/SubcontractorsPage'), 'SubcontractorsPage');
const FinanceSyncPage = lazyPage(() => import('@/modules/service/pages/finance-sync/FinanceSyncPage'), 'FinanceSyncPage');

// ── Finance placeholder ──
const FinanceDashboardPage = lazyPage(() => import('@/modules/finance/pages/FinanceDashboardPage'), 'FinanceDashboardPage');

// ── Settings ──
const DealershipSettingsPage = lazyPage(() => import('@/modules/tenant/pages/settings/DealershipSettingsPage'), 'DealershipSettingsPage');
const TemplateListPage = lazyPage(() => import('@/modules/tenant/pages/settings/TemplateListPage'), 'TemplateListPage');
const TemplateViewPage = lazyPage(() => import('@/modules/tenant/pages/settings/TemplateViewPage'), 'TemplateViewPage');
const InboundEmailSettingsPage = lazyPage(() => import('@/modules/tenant/pages/settings/InboundEmailSettingsPage'), 'InboundEmailSettingsPage');

/**
 * Tenant routes - only loaded when accessing a tenant subdomain
 * (e.g., http://stws.marine-dms.local:3000/dashboard)
 *
 * All module pages are lazy-loaded. Module routes are gated by ModuleGate
 * so disabled modules never download their JS chunks.
 */
export function TenantRoutes() {
  return (
    <ModuleProvider>
      <Routes>
        {/* Tenant login (public) */}
        <Route path="/login" element={<TenantLoginPage />} />

        {/* Password reset (public, no auth) */}
        <Route path="/forgot-password" element={<TenantForgotPasswordPage />} />
        <Route path="/reset-password/check-email" element={<TenantResetPasswordCheckEmailPage />} />
        <Route path="/reset-password" element={<TenantResetPasswordPage />} />
        <Route path="/reset-password/changed" element={<TenantResetPasswordChangedPage />} />

        {/* Public invitation acceptance (no auth required) */}
        <Route path="/invite/:token" element={<Suspense fallback={<PageSkeleton />}><InviteAcceptPage /></Suspense>} />

        {/* Protected tenant routes */}
        <Route
          path="/"
          element={
            <TenantProtectedRoute>
              <TenantLayout />
            </TenantProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard" element={<Suspense fallback={<PageSkeleton />}><TenantDashboard /></Suspense>} />

          {/* CRM Module — gated */}
          <Route path="crm" element={<ModuleGate module="crm"><CrmDashboardPage /></ModuleGate>} />
          <Route path="crm/activity" element={<ModuleGate module="crm"><ActivityPage /></ModuleGate>} />
          {/* #1157 — /crm/tasks was #158's Today/Overdue list, replaced by the
              Activity page. It redirects rather than 404s because the CRM
              dashboard's "Tasks Due Today" tile and MyTasksCard's "View all"
              both point at it, as do any bookmarks reps already have. The
              query string reproduces exactly what that page showed: my open
              tasks due by tonight. */}
          <Route
            path="crm/tasks"
            element={<Navigate to="/crm/activity?who=mine&kind=task&due=today" replace />}
          />
          <Route path="crm/enquiries" element={<ModuleGate module="crm"><EnquiriesInboxPage /></ModuleGate>} />
          <Route path="crm/enquiries/:id" element={<ModuleGate module="crm"><EnquiryDetailPage /></ModuleGate>} />
          <Route path="crm/customers" element={<ModuleGate module="crm"><CustomersListPage /></ModuleGate>} />
          {/* #908 — same page, leads segment locked; owns the bare /crm/leads
              path that used to be a #552 legacy redirect to /crm/deals (the
              deeper /crm/leads/* bookmark redirects below still stand). */}
          <Route path="crm/leads" element={<ModuleGate module="crm"><CustomersListPage leadsOnly /></ModuleGate>} />
          <Route path="crm/customers/:id" element={<ModuleGate module="crm"><CustomerDetailPage /></ModuleGate>} />
          <Route path="crm/pipelines" element={<ModuleGate module="crm"><PipelinesPage /></ModuleGate>} />
          <Route path="crm/deals/kanban" element={<ModuleGate module="crm"><LeadKanbanPage /></ModuleGate>} />
          <Route path="crm/deals/deleted" element={<ModuleGate module="crm"><LeadsListPage /></ModuleGate>} />
          <Route path="crm/deals/:id" element={<ModuleGate module="crm"><LeadDetailPage /></ModuleGate>} />
          <Route path="crm/deals" element={<ModuleGate module="crm"><LeadsListPage /></ModuleGate>} />
          {/* Legacy /crm/leads bookmarks (#552) */}
          <Route path="crm/leads/kanban" element={<Navigate to="/crm/deals/kanban" replace />} />
          <Route path="crm/leads/deleted" element={<Navigate to="/crm/deals/deleted" replace />} />
          <Route path="crm/leads/:id" element={<LegacyLeadRedirect />} />
          <Route path="crm/quotes/deleted" element={<ModuleGate module="crm"><QuotesListPage /></ModuleGate>} />
          <Route path="crm/quotes/:id" element={<ModuleGate module="crm"><QuoteDetailPage /></ModuleGate>} />
          <Route path="crm/quotes" element={<ModuleGate module="crm"><QuotesListPage /></ModuleGate>} />
          <Route path="crm/trade-ins/:id/appraise" element={<ModuleGate module="crm"><TradeInAppraisalPage /></ModuleGate>} />

          {/* Inventory Module — gated */}
          <Route path="inventory" element={<ModuleGate module="inventory"><InventoryDashboardPage /></ModuleGate>} />
          <Route path="inventory/boats" element={<ModuleGate module="inventory"><AdminBoatListPage /></ModuleGate>} />
          <Route path="inventory/boats/:id/:tab?" element={<ModuleGate module="inventory"><BoatDetailPage /></ModuleGate>} />
          <Route path="inventory/engines" element={<ModuleGate module="inventory"><AdminEngineListPage /></ModuleGate>} />
          <Route path="inventory/engines/:id/:tab?" element={<ModuleGate module="inventory"><EngineDetailPage /></ModuleGate>} />
          <Route path="inventory/trailers" element={<ModuleGate module="inventory"><AdminTrailerListPage /></ModuleGate>} />
          <Route path="inventory/trailers/:id/:tab?" element={<ModuleGate module="inventory"><TrailerDetailPage /></ModuleGate>} />
          <Route path="inventory/settings" element={<ModuleGate module="inventory"><InventorySettingsPage /></ModuleGate>} />
          <Route path="inventory/wp-sync" element={<ModuleGate module="inventory"><WpSyncPage /></ModuleGate>} />
          <Route path="inventory/reports" element={<ModuleGate module="inventory"><InventoryReportsPage /></ModuleGate>} />
          <Route path="inventory/reports/:reportType" element={<ModuleGate module="inventory"><InventoryReportsPage /></ModuleGate>} />

          {/* Reporting module (AI Reporting, PAGE-01) */}
          <Route path="reporting" element={<ModuleGate module="reporting"><ReportingAccessGate><AiReportingPage /></ReportingAccessGate></ModuleGate>} />

          {/* Service Module — gated */}
          <Route path="service" element={<Navigate to="/service/work-orders" replace />} />
          <Route path="service/work-orders" element={<ModuleGate module="service"><WorkOrdersListPage /></ModuleGate>} />
          <Route path="service/work-orders/:id" element={<ModuleGate module="service"><WorkOrderDetailPage /></ModuleGate>} />
          <Route path="service/subcontractors" element={<ModuleGate module="service"><SubcontractorsPage /></ModuleGate>} />
          <Route path="service/finance-sync" element={<ModuleGate module="service"><FinanceSyncPage /></ModuleGate>} />

          {/* Finance Module — gated */}
          <Route path="finance" element={<ModuleGate module="finance"><FinanceDashboardPage /></ModuleGate>} />

          {/* Settings (core — always available) */}
          <Route path="settings/dealership/:tab?" element={<Suspense fallback={<PageSkeleton />}><DealershipSettingsPage /></Suspense>} />
          <Route path="settings/inventory" element={<Navigate to="/inventory/settings" replace />} />
          <Route path="settings/templates" element={<Suspense fallback={<PageSkeleton />}><TemplateListPage /></Suspense>} />
          <Route path="settings/templates/:id" element={<Suspense fallback={<PageSkeleton />}><TemplateViewPage /></Suspense>} />
          <Route path="settings/roles" element={<Suspense fallback={<PageSkeleton />}><RolesPage /></Suspense>} />
          <Route path="settings/inbound-email" element={<Suspense fallback={<PageSkeleton />}><InboundEmailSettingsPage /></Suspense>} />

          {/* Legacy/Placeholder routes */}
          <Route path="boats" element={<Navigate to="/inventory/boats" replace />} />
          <Route path="employees" element={<Suspense fallback={<PageSkeleton />}><EmployeesListPage /></Suspense>} />
          <Route path="employees/:id" element={<Suspense fallback={<PageSkeleton />}><EmployeeDetailPage /></Suspense>} />

          {/* Profile */}
          <Route path="profile/:tab?" element={<Suspense fallback={<PageSkeleton />}><TenantProfilePage /></Suspense>} />
        </Route>

        {/* 404 */}
        <Route path="*" element={<Suspense fallback={<PageSkeleton />}><NotFoundPage /></Suspense>} />
      </Routes>
    </ModuleProvider>
  );
}
